import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, useIntl } from 'react-intl';
import { Tooltip } from 'react-bootstrap';

import intlShape from '@/shared/utils/intlShape';
import { formatListValue } from '@/shared/components/FormattedListValue';
import TaskStatusToggle from '../TaskStatusToggle';

import messages from '../taskMessages';
import FormattedDateShort from '../../../shared/components/FormattedDateShort';
import FormattedDateTime from '@/shared/components/FormattedDateTime';

const cellTooltip = (column, value, assignedUser, intl) =>
  value ? (
    <Tooltip id={value.id}>
      <div>
        <span className="bold">
          {value.name ||
            formatListValue({ id: column.id, list: 'taskTemplate', fallback: column.name, intl })}
        </span>
      </div>
      {value.deadline && (
        <div>
          <span className="bold">
            {intl.formatMessage(messages.Deadline)}
            :&nbsp;
          </span>
          <FormattedDateShort value={value.deadline} />
        </div>
      )}
      {value.completedDate && (
        <div>
          <span className="bold">
            {intl.formatMessage(messages.CompletedDate)}
            :&nbsp;
          </span>
          <FormattedDateShort value={value.completedDate} />
        </div>
      )}
      <div>
        <span className="bold">
          {intl.formatMessage(messages.AssignedTo)}
          :&nbsp;
        </span>
        {assignedUser || intl.formatMessage(messages.Unassigned)}
      </div>
    </Tooltip>
  ) : null;
const TaskOverviewCell = ({
  taskId,
  column,
  value,
  assignedUser,
  displayMode,
  onTasksUpdated,
  onOpenDetail,
  onAddRegistration,
  intl,
}) => {
  const extraInfo = useMemo(() => {
    if (!value) return null;
    if (
      displayMode === 'dateDone' &&
      value.status === 2 &&
      value.completedDate &&
      !value.isBlocked
    ) {
      return (
        <span className="margin5L">
          <FormattedDateShort value={value.completedDate} />
        </span>
      );
    }
    if (displayMode === 'deadline' && value.deadline) {
      return (
        <span className="margin5L">
          <FormattedDateShort value={value.deadline} />
        </span>
      );
    }
    if (displayMode === 'executionDate' && value.executionDate) {
      return (
        <span className="margin5L">
          <FormattedDateShort value={value.executionDate} />
        </span>
      );
    }
    if (displayMode === 'assignedTo') {
      return (
        <span className="margin5L">
          {assignedUser ? assignedUser : <em>{intl.formatMessage(messages.Unassigned)}</em>}
        </span>
      );
    }
    return null;
  }, [displayMode, value, assignedUser, intl]);

  const overlay = useMemo(() => cellTooltip(column, value, assignedUser, intl), [
    column,
    value,
    assignedUser,
    intl,
  ]);
  const extendedValue = useMemo(
    () =>
      value
        ? {
            ...value,
            taskLevel: 1,
            taskType: column.type,
          }
        : null,
    [value, column.type],
  );

  const cellClassName = displayMode === 'assignedTo' ? 'text-left no-wrap' : 'text-center no-wrap';

  if (!value) return <td className={cellClassName}></td>;

  return (
    <td className={cellClassName}>
      <div
        style={
          displayMode === 'assignedTo'
            ? { display: 'flex', justifyContent: 'flex-start' }
            : undefined
        }
      >
        <TaskStatusToggle
          id={`TaskStatusToggle-${taskId}-${column.id}`}
          value={extendedValue}
          overlay={overlay}
          onTasksUpdated={onTasksUpdated}
          title={intl.formatMessage(messages.ChangeSubtaskStatus)}
          onOpenDetail={onOpenDetail}
          extraInfo={extraInfo}
          onAddRegistration={onAddRegistration}
        />
      </div>
    </td>
  );
};
TaskOverviewCell.propTypes = {
  taskId: PropTypes.string.isRequired,
  column: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.number,
  }).isRequired,
  value: PropTypes.shape({
    id: PropTypes.string,
    deadline: PropTypes.string,
    status: PropTypes.number,
    hasAttachments: PropTypes.bool,
  }),
  displayMode: PropTypes.oneOf(['status', 'dateDone', 'deadline', 'executionDate', 'assignedTo'])
    .isRequired,
  assignedUser: PropTypes.string,
  onTasksUpdated: PropTypes.func.isRequired,
  onOpenDetail: PropTypes.func,
  onAddRegistration: PropTypes.func,
  intl: intlShape.isRequired,
};
TaskOverviewCell.defaultProps = {
  value: null,
  onOpenDetail: null,
  assignedUser: null,
  onAddRegistration: null,
};

export default memo(injectIntl(TaskOverviewCell));
